<template>
  <el-row :gutter="15">
    <el-col
      :xs="12"
      :sm="8"
      :md="7"
      :lg="6"
      :xl="4"
    >
      <el-card
        shadow="never"
        header="Lists"
      >
        <el-menu
          :default-active="activeList"
          class="border-0"
          @select="index => { activeList = index }"
        >
          <el-menu-item index="My Opportunities">
            <FontAwesomeIcon
              :icon="[ 'fal', 'bullseye-arrow' ]"
              size="lg"
              fixed-width
            />
            <span class="pl-3">My Opportunities</span>
          </el-menu-item>

          <el-menu-item index="All Opportunities">
            <FontAwesomeIcon
              :icon="[ 'fal', 'bullseye' ]"
              size="lg"
              fixed-width
            />
            <span class="pl-3">All Opportunities</span>
          </el-menu-item>

          <el-menu-item index="Stale Opportunities">
            <FontAwesomeIcon
              :icon="[ 'fal', 'dot-circle' ]"
              size="lg"
              fixed-width
            />
            <span class="pl-3">Stale Opportunities</span>
          </el-menu-item>
        </el-menu>
      </el-card>
    </el-col>

    <el-col
      :xs="12"
      :sm="16"
      :md="17"
      :lg="18"
      :xl="20"
    >
      <Filters
        class="mb-3"
        :resource="$sdk.account_lead"
        :filters.sync="filters"
        @ready="loadOpportunities"
        @apply="reloadOpportunities"
      />

      <el-card
        v-loading="isLoading"
        shadow="never"
      >
        <OpportunityTable :opportunities.sync="opportunities">
          <template v-slot:peek="{ opportunity }">
            <OpportunityPeek :opportunity="opportunity" />
          </template>

          <template
            v-if="canUpdate"
            v-slot:actions="{ opportunity }"
          >
            <template v-if="opportunity.status !== 'lost' && opportunity.status !== 'trading'">
              <el-button
                size="mini"
                :disabled="opportunity.status === 'pending'"
                @click.native="edit(opportunity)"
              >
                Edit
              </el-button>

              <el-button
                size="mini"
                type="danger"
                :disabled="!isOwnOpportunity(opportunity)"
                @click.native="markAsLost(opportunity)"
              >
                Lost
              </el-button>
            </template>

            <template v-else-if="opportunity.status === 'trading'">
              <span class="text-muted font-italic">Began Trading {{ opportunity.updated_at | moment('from', 'now') }}</span>
            </template>

            <template v-else>
              <span class="text-muted font-italic">Opportunity Lost {{ opportunity.updated_at | moment('from', 'now') }}</span>
            </template>
          </template>
        </OpportunityTable>

        <el-pagination
          class="text-center pt-3"
          :current-page.sync="opportunities.page"
          :page-size.sync="opportunities.limit"
          layout="total, sizes, prev, pager, next, jumper"
          :total="opportunities.total"
          @size-change="reloadOpportunities"
          @current-change="reloadOpportunities"
        />
      </el-card>

      <el-dialog
        title="Edit Opportunity"
        width="70vw"
        top="2vh"
        :visible.sync="isEditingOpportunity"
        :close-on-click-modal="!isSavingOpportunity"
        :close-on-press-escape="!isSavingOpportunity"
        :show-close="!isSavingOpportunity"
      >
        <OpportunityForm :opportunity="opportunity" />

        <template #footer>
          <el-button
            :disabled="isSavingOpportunity"
            @click.native="isEditingOpportunity = false"
          >
            Cancel
          </el-button>

          <el-button
            type="primary"
            :loading="isSavingOpportunity"
            @click.native="save"
          >
            Confirm
          </el-button>
        </template>
      </el-dialog>

      <el-dialog
        title="Lost Opportunity"
        :visible.sync="isMarkingAsLost"
        :close-on-click-modal="!isSavingOpportunity"
        :close-on-press-escape="!isSavingOpportunity"
        :show-close="!isSavingOpportunity"
      >
        <MarkAsLostForm :opportunity="opportunity" />

        <template #footer>
          <el-button
            @click.native="isMarkingAsLost = false"
          >
            Cancel
          </el-button>

          <el-button
            type="danger"
            :loading="isSavingOpportunity"
            @click.native="markAsLost"
          >
            Confirm
          </el-button>
        </template>
      </el-dialog>
    </el-col>
  </el-row>
</template>

<script>
import Filters from '@/components/Filters/Complex'
import OpportunityTable from '@/components/Application/Sales/Opportunities/Table'
import OpportunityPeek from '@/components/Application/Sales/Opportunities/Opportunity/Peek'
import OpportunityForm from '@/components/Application/Sales/Opportunities/Opportunity/Form'
import MarkAsLostForm from '@/components/Application/Sales/Opportunities/Opportunity/Form/MarkAsLostForm'
import Filter, { IS, IS_NOT, IS_LESS_THAN } from '@/plugins/sdk/Filter'
import { CREATED, UPDATED, DELETED, RESTORED } from '@/plugins/sdk/Events'
import { UPDATE, DELETE } from '@/plugins/user'

const DEFAULT_LIST = 'My Opportunities'

export default {
  components: {
    Filters,
    OpportunityTable,
    OpportunityPeek,
    OpportunityForm,
    MarkAsLostForm
  },
  data () {
    return {
      actions: [
        ...this.$parent.actions,
        {
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: this.reloadOpportunities
        }
      ],
      isLoadingMyOpportunities: false,
      isLoadingAllOpportunities: false,
      isLoadingStaleOpportunities: false,
      isEditingOpportunity: false,
      isSavingOpportunity: false,
      isMarkingAsLost: false,
      portfolios: [],
      myOpportunities: [],
      myOpportunityFilters: [
        new Filter('status', IS_NOT, [ 'lost' ])
      ],
      allOpportunities: [],
      allOpportunityFilters: [],
      staleOpportunities: [],
      staleOpportunityFilters: [
        new Filter('status', IS_NOT, [ 'lost' ], true, true),
        new Filter('events.due_at', IS_LESS_THAN, this.$moment().subtract(6, 'months'), true, true)
      ],
      opportunity: null,
      activeList: undefined
    }
  },
  computed: {
    isLoading () {
      switch (this.activeList) {
        case 'My Opportunities':
          return this.isLoadingMyOpportunities
        case 'All Opportunities':
          return this.isLoadingAllOpportunities
        case 'Stale Opportunities':
          return this.isLoadingStaleOpportunities
        default:
          return false
      }
    },
    opportunities () {
      switch (this.activeList) {
        case 'My Opportunities':
          return this.myOpportunities
        case 'All Opportunities':
          return this.allOpportunities
        case 'Stale Opportunities':
          return this.staleOpportunities
        default:
          return []
      }
    },
    filters: {
      get () {
        switch (this.activeList) {
          case 'My Opportunities':
            return this.myOpportunityFilters
          case 'All Opportunities':
            return this.allOpportunityFilters
          case 'Stale Opportunities':
            return this.staleOpportunityFilters
          default:
            return []
        }
      },
      set (value) {
        switch (this.activeList) {
          case 'My Opportunities':
            this.myOpportunityFilters = value
            break
          case 'All Opportunities':
            this.allOpportunityFilters = value
            break
          case 'Stale Opportunities':
            this.staleOpportunityFilters = value
            break
        }
      }
    },
    portfolioFilter () {
      return new Filter('portfolio', IS, this.$_.map(this.portfolios, 'id'))
    },
    canUpdate () {
      return this.$user.can(UPDATE, this.$sdk.account_lead)
    },
    canDelete () {
      return this.$user.can(DELETE, this.$sdk.account_lead)
    }
  },
  watch: {
    activeList (value) {
      if (this.$route.query.list !== value) {
        this.$router.replace({ query: { list: value } })
      }
    }
  },
  created () {
    this.activeList = this.$route.query.list || DEFAULT_LIST
  },
  mounted () {
    this
      .$sdk
      .account_lead
      .on([ CREATED, UPDATED, DELETED, RESTORED ], this.reloadOpportunities, this)
  },
  methods: {
    loadOpportunities () {
      switch (this.activeList) {
        case 'My Opportunities':
          this.getMyOpportunities()
          break
        case 'All Opportunities':
          this.getAllOpportunities()
          break
        case 'Stale Opportunities':
          this.getStaleOpportunities()
      }
    },
    async reloadOpportunities () {
      switch (this.activeList) {
        case 'My Opportunities':
          this.reloadMyOpportunities()
          break
        case 'All Opportunities':
          this.reloadAllOpportunities()
          break
        case 'Stale Opportunities':
          this.reloadStaleOpportunities()
      }
    },
    async getMyOpportunities () {
      this.isLoadingMyOpportunities = true

      this.portfolios = (await this.$sdk.session.read(undefined, builder => {
        builder.include = [ 'profile.portfolios' ]
        builder.limit = 500
      })).profile.portfolios

      if (this.portfolios.length > 0) {
        this.myOpportunities = await this.$sdk.account_lead.list(builder => {
          builder.include = [ 'account.portfolio', 'incumbent_carriers.carrier', 'suggested_carriers.carrier', 'freights.type' ]
          builder.filters = [
            this.portfolioFilter,
            ...this.myOpportunityFilters
          ]
          builder.order = 'account.name:asc'
        })
      }

      this.isLoadingMyOpportunities = false
    },
    async getAllOpportunities () {
      this.isLoadingAllOpportunities = true

      this.allOpportunities = await this.$sdk.account_lead.list(builder => {
        builder.include = [ 'account.portfolio', 'incumbent_carriers.carrier', 'suggested_carriers.carrier', 'freights.type' ]
        builder.filters = this.allOpportunityFilters
        builder.order = 'account.name:asc'
      })

      this.isLoadingAllOpportunities = false
    },
    async getStaleOpportunities () {
      this.isLoadingStaleOpportunities = true

      this.staleOpportunities = await this.$sdk.account_lead.list(builder => {
        builder.include = [ 'account.portfolio', 'incumbent_carriers.carrier', 'suggested_carriers.carrier', 'freights.type' ]
        builder.filters = this.staleOpportunityFilters
        builder.order = 'account.name:asc'
      })

      this.isLoadingStaleOpportunities = false
    },
    async reloadMyOpportunities () {
      this.isLoadingMyOpportunities = true

      try {
        await this.opportunities.reload(builder => {
          builder.filters = [
            this.portfolioFilter,
            ...this.filters
          ]
        })
      } catch {
        this.$message.error('Failed to load My Opportunities')
      }

      this.isLoadingMyOpportunities = false
    },
    async reloadAllOpportunities () {
      this.isLoadingAllOpportunities = true

      try {
        await this.opportunities.reload(builder => {
          builder.filters = [
            ...this.filters
          ]
        })
      } catch {
        this.$message.error('Failed to load All Opportunities')
      }

      this.isLoadingAllOpportunities = false
    },
    async reloadStaleOpportunities () {
      this.isLoadingStaleOpportunities = true

      try {
        await this.opportunities.reload(builder => {
          builder.filters = [
            ...this.filters
          ]
        })
      } catch {
        this.$message.error('Failed to load Stale Opportunities')
      }

      this.isLoadingStaleOpportunities = false
    },
    isOwnOpportunity (opportunity) {
      let is = false

      for (var i = this.portfolios.length - 1; i >= 0; i--) {
        if (!is) {
          is = this.portfolios[i].id === opportunity.portfolio.id
        } else {
          break
        }
      }

      return is
    },
    edit (opportunity) {
      if (opportunity instanceof this.$sdk.account_lead) {
        this.opportunity = opportunity.clone()
      }

      this.isEditingOpportunity = true
    },
    async save () {
      this.isSavingOpportunity = true

      await this.opportunity.update()

      this.$message.success(`Successfully updated ${this.opportunity.account.name}`)

      this.isEditingOpportunity = false
      this.isSavingOpportunity = false
      this.opportunity = null
    },
    async markAsLost (opportunity) {
      const note = Object.assign(new this.$sdk.note(), {
        resource: opportunity.account,
        resource_uuid: opportunity.account.id
      })

      this
        .$prompt('Enter the reason for losing this Opportunity', 'Lost Opportunity', {
          confirmButtonText: 'Mark as Lost',
          cancelButtonText: 'Cancel',
          inputType: 'textarea'
        })
        .then(async ({ value }) => {
          note.content = value

          opportunity = Object.assign(opportunity.clone(), {
            status: 'lost'
          })

          this.isSavingOpportunity = true

          try {
            await opportunity.update()
            await note.create()

            this.$message.success(`Successfully updated ${opportunity.account.name}`)
          } finally {
            this.isSavingOpportunity = false
          }
        })
    }
  }
}
</script>
